import type { Dict } from "../types";

export const auth: Dict = {
  en: {

    // Auth toast messages (errorToast / successToast wrapper)
    "Auth.toast.signInFailed": "Could not sign in",
    "Auth.toast.signInSuccess": "Signed in",
    "Auth.toast.signInSuccessDesc": "Welcome back",
    "Auth.toast.signUpFailed": "Could not create account",
    "Auth.toast.accountCreated": "Account created",
    "Auth.toast.welcomeMessage": "You are now logged in",
    "Auth.toast.accessDenied": "Access denied",
    "Auth.toast.accountReservedAdmin": "This account is reserved for administration. Please use a client account to book.",
    "Auth.toast.invalidPhoneNumber": "Please enter a valid phone number.",
    "Auth.toast.phoneRequired": "Please enter your phone number.",
    "Auth.toast.salonContextRequired": "Please use your salon's booking link to create an account.",
    "InviteAccept.title": "Create your Flowcut AI account",
    "InviteAccept.subtitle": "Welcome! Complete this information to activate your subscription.",
    "InviteAccept.salonName": "Salon name",
    "InviteAccept.salonNamePlaceholder": "Salon Élégance",
    "InviteAccept.ownerName": "Your full name",
    "InviteAccept.ownerNamePlaceholder": "Marie Tremblay",
    "InviteAccept.phone": "Phone",
    "InviteAccept.email": "Email",
    "InviteAccept.emailLocked": "The email you used for your Stripe payment.",
    "InviteAccept.password": "Password",
    "InviteAccept.passwordPlaceholder": "Minimum 10 characters",
    "InviteAccept.passwordHint": "Min. 10 characters, with an uppercase letter, a lowercase letter, a number and a special character",
    "InviteAccept.confirmPassword": "Confirm password",
    "InviteAccept.confirmPasswordPlaceholder": "Re-enter your password",
    "InviteAccept.submit": "Create my account",
    "InviteAccept.submitting": "Creating...",
    "InviteAccept.invalidTitle": "Invalid invitation",
    "InviteAccept.invalidDescription": "This invitation link has expired or is no longer valid.",
    "InviteAccept.backHome": "Back to home",
    "InviteAccept.verifyErrorTitle": "Verification unavailable",
    "InviteAccept.verifyErrorDescription": "We couldn't verify your invitation right now. This is a temporary issue on our end, not a problem with your link.",
    "InviteAccept.retry": "Try again",
    "Auth.toast.googleSignInFailed": "Google sign-in failed. Please try again.",
    "Auth.toast.emailVerified": "Email verified",
    "Auth.toast.verificationEmailSent": "Verification email sent. Check your inbox.",
    "Auth.toast.verificationEmailFailed": "Could not send the email. Please try again.",
    "Auth.verifyBanner.title": "Please verify your email address",
    "Auth.verifyBanner.description": "Check your inbox for the confirmation link.",
    "Auth.verifyBanner.resend": "Resend",
    "Auth.toast.clockSkew": "Your computer's clock appears to be incorrect. Turn on automatic time and time zone, then sign in again.",
    "Auth.toast.emailVerifiedDesc": "Your email address has been confirmed. You can now log in.",
    "Auth.toast.passwordResetRequestFailed": "Could not send password reset email",
    "Auth.toast.passwordResetSent": "Password reset email sent",
    "Auth.toast.passwordResetSentDesc": "Password reset instructions have been sent to your email.",
    "Auth.toast.passwordResetSuccess": "Password reset",
    "Auth.toast.passwordResetSuccessDesc": "Your password has been reset successfully.",
    "Auth.toast.passwordResetFailed": "Could not reset password",
    "Auth.toast.passwordsMismatch": "Passwords do not match.",
    "Auth.toast.invalidResetLink": "The reset link is invalid or expired. Please request a new link.",
    "Auth.toast.resetLinkExpired": "Your reset link has expired. Please request a new one.",
    "Auth.toast.resetPasswordLeaked": "This password appears in a known data breach. Choose a different one, your reset link is still valid.",
    "Auth.toast.resetSamePassword": "This password is the same as your current one. Choose a different one, your reset link is still valid.",
    "Auth.toast.resetReauthNeeded": "For your security, please request a new reset link to change your password.",
    "Auth.toast.missingResetSession": "Authentication session missing. Please click the reset link again.",
    "Auth.toast.twoFactorRequired": "Two-factor authentication required",
    "Auth.toast.twoFactorRequiredDesc": "Please enter your verification code",
    "Auth.toast.verificationFailed": "Verification failed",
    "Auth.toast.setup2FAFailed": "Could not set up two-factor authentication",
    "Auth.toast.disable2FAFailed": "Could not disable two-factor authentication",
    "Auth.toast.accountSuspended": "Account suspended",
    "Auth.toast.inviteAcceptFailed": "Could not accept invitation",
    "Auth.toast.inviteAcceptSuccess": "Account created successfully. Redirecting...",
    "Auth.toast.passwordTooShort": "Password must be at least 10 characters",
    "Auth.password.missingUppercase": "Password must contain at least one uppercase letter (A to Z).",
    "Auth.password.missingLowercase": "Password must contain at least one lowercase letter (a to z).",
    "Auth.password.missingDigit": "Password must contain at least one number.",
    "Auth.password.missingSpecial": "Password must contain at least one special character, for example ! ? # $ % & * -",
    "Auth.passwordRequirements.length": "At least 10 characters",
    "Auth.passwordRequirements.uppercase": "An uppercase letter (A to Z)",
    "Auth.passwordRequirements.lowercase": "A lowercase letter (a to z)",
    "Auth.passwordRequirements.digit": "A number",
    "Auth.passwordRequirements.special": "A special character, for example ! ? # $ % & * -",
    "Auth.passwordRequirements.met": "(met)",
    "Auth.passwordRequirements.unmet": "(missing)",
    "Auth.toast.captchaRequired": "Please complete the anti-robot verification.",
    "Auth.toast.notAuthenticated": "Not authenticated",
    "Auth.toast.createAdminFailed": "Could not create admin",
    "Auth.toast.createAdminUnexpected": "Unexpected error while creating admin",
    "Auth.toast.createAdminSuccess": "Admin created successfully",

    // Session
    "session.invalidatedTitle": "Session Ended",
    "session.invalidatedDesc": "You were logged out because your account was accessed from another device.",
    "session.expiredTitle": "Session Expired",
    "session.expiredDesc": "Your session has expired after 24 hours. Please sign in again.",
    "session.jwtExpiredTitle": "Session Expired",
    "session.jwtExpiredDesc": "Your session has timed out. Please sign in again.",

    // ClientAuth
    "ClientAuth.emailPlaceholder": "Email",
    "ClientAuth.passwordPlaceholder": "Password",
    "ClientAuth.forgotPassword": "Forgot your password?",
    "ClientAuth.loading": "Loading...",
    "ClientAuth.signIn": "Sign In",
    "ClientAuth.backToHome": "Back to Home",
    "ClientAuth.loginSuccessDesc": "You have successfully logged in.",
    "ClientAuth.otp.codeStepTitle": "Enter your code",
    "ClientAuth.otp.codeSentTo": "We sent a 6-digit code to",
    "ClientAuth.otp.codeLabel": "Verification code",
    "ClientAuth.otp.verify": "Verify",
    "ClientAuth.otp.resend": "Resend code",
    "ClientAuth.otp.resendIn": "Resend in",
    "ClientAuth.otp.changeEmail": "Not this email? Change it",
    "ClientAuth.otp.sendFailed": "Could not send the code. Please try again.",
    "ClientAuth.otp.rateLimited": "A code was just sent to this address. You can request another one in {seconds} s.",
    "ClientAuth.otp.invalidCode": "That code is invalid or has expired. Please try again.",
    "ClientAuth.wall.titleBook": "Log in or sign up to book",
    "ClientAuth.wall.titleHistory": "Log in to access your account",
    "ClientAuth.wall.subtitle": "To continue, we'll need to verify it's really you.",
    "ClientAuth.wall.emailHelp": "We'll send you a verification code.",
    "ClientAuth.wall.continue": "Continue",
    "ClientAuth.pw.title": "Enter your password",
    "ClientAuth.pw.subtitle": "One last step to confirm it's you.",
    "ClientAuth.setPw.title": "Create your password",
    "ClientAuth.setPw.subtitle": "Your account does not have a password yet. Choose one now and you will be able to sign in with it from now on.",
    "ClientAuth.setPw.submit": "Save and continue",
    "ClientAuth.setPw.googleAlt": "Or, if you usually sign in with Google:",
    "ClientAuth.setPw.refused": "This password was refused. Try another one.",
    "ClientAuth.password.reauthNeeded": "For your security, please confirm your email again before choosing your password.",
    "ClientAuth.setPw.leaked": "This password appears in a known data breach, so it cannot be used. Choose one you do not use anywhere else, for example three unrelated words with a number and a symbol.",
    // ClientAuthPanel, "roleRetry" step: the role lookup failed (never a refusal)
    "ClientAuth.roleCheck.title": "Connection problem",
    "ClientAuth.roleCheck.failed": "A connection problem prevented us from checking your account. Please try again.",
    "ClientAuth.roleCheck.retry": "Try again",
    // /auth/client is login-only: an unknown contact never signs up here (a
    // client is born from a booking, spec §5-bis case 4). Shown in place of
    // CompleteSignupPanel when onNewClient fires on this standalone page.
    "ClientAuth.noAccountFound.title": "No account found",
    "ClientAuth.noAccountFound.description": "Book an appointment to create one.",
    // Flux de code maison (lot 7 du tunnel /book) : un message par refus de l'edge client-email-code.
    "ClientAuth.code.tooManyRequests": "Too many code requests from this device. Please wait a few minutes before trying again.",
    "ClientAuth.code.serviceBusy": "Codes cannot be sent right now. Please try again in a few minutes.",
    "ClientAuth.code.captchaFailed": "The anti-bot check did not go through. Please try again.",
    "ClientAuth.code.invalidSalon": "This booking link is no longer valid. Please contact your salon.",
    "ClientAuth.code.wrong": "This code is incorrect. You have {n} attempts left.",
    "ClientAuth.code.wrongOne": "This code is incorrect. You have 1 attempt left.",
    "ClientAuth.code.tooManyAttempts": "Too many attempts for this code. Request a new code.",
    "ClientAuth.code.expired": "This code has expired. Request a new code.",
    "ClientAuth.code.staffAccount": "This address belongs to a salon staff account. To book, please use another email address.",
    "ClientAuth.code.sessionFailed": "Your code is valid, but we could not sign you in. Please try again.",
    "ClientAuth.code.network": "Could not connect. Check your network, then try again.",
    "ClientAuth.code.incomplete": "Enter the 6 digits of the code.",
    "ClientAuth.continueWithGoogle": "Continue with Google",
    "ClientAuth.or": "OR",
    "ClientAuth.noSalonHint": "Don't have an account? Use your salon's booking link to sign up.",
    "ClientAuth.backToBooking": "Back to Booking",

    // CompleteSignupPanel ("Complete your signup": mounted after ClientAuthPanel.onNewClient)
    "CompleteSignup.title": "Complete your signup",
    "CompleteSignup.subtitleEmail": "Just a few more details to finish creating your account.",
    "CompleteSignup.subtitleGoogle": "Almost done: just a couple more details.",
    "CompleteSignup.emailLabel": "Email",
    "CompleteSignup.fullNameLabel": "Full name",
    "CompleteSignup.phoneLabel": "Phone number (optional)",
    "CompleteSignup.passwordLabel": "Password",
    "CompleteSignup.submitButton": "Complete signup",
    "CompleteSignup.success": "Your account is ready!",
    "CompleteSignup.errors.fullNameRequired": "Please enter your full name.",
    "CompleteSignup.errors.phoneInvalid": "Please enter a valid phone number.",
    "CompleteSignup.errors.saveFailed": "Something went wrong. Please try again.",
    "CompleteSignup.errors.profileSaveFailed": "Your password is saved, but your details could not be. Please try again.",

    // OwnerAuth
    "OwnerAuth.welcomeBack": "Welcome back",
    "OwnerAuth.signInPrompt": "Sign in to manage your salon",
    "OwnerAuth.email": "Email",
    "OwnerAuth.emailPlaceholder": "you@example.com",
    "OwnerAuth.password": "Password",
    "OwnerAuth.passwordPlaceholder": "••••••••",
    "OwnerAuth.loading": "Loading...",
    "OwnerAuth.signIn": "Sign In",
    "OwnerAuth.forgotPassword": "Forgot your password?",
    "OwnerAuth.backToHome": "Back to Home",
    "OwnerAuth.success": "Success!",
    "OwnerAuth.successDesc": "You've been logged in successfully.",
    "OwnerAuth.accessDenied": "Access Denied",
    "OwnerAuth.accessDeniedDesc":
      "This login page is for salon owners only. Please use the correct login page for your role.",
    "OwnerAuth.error": "Error",
    "OwnerAuth.errorDesc": "An error occurred during authentication",
    "OwnerAuth.accountSuspended": "Account Suspended: Payment Failed",
    "OwnerAuth.accountSuspendedDesc":
      "Your salon account has been suspended due to payment failure. Please update your payment method to restore access.",

    // AdminAuth
    "AdminAuth.welcomeBack": "Welcome back",
    "AdminAuth.signInPrompt": "Sign in to access the dashboard",
    "AdminAuth.email": "Email",
    "AdminAuth.emailPlaceholder": "admin@example.com",
    "AdminAuth.password": "Password",
    "AdminAuth.passwordPlaceholder": "••••••••",
    "AdminAuth.loading": "Loading...",
    "AdminAuth.signIn": "Sign In",
    "AdminAuth.forgotPassword": "Forgot your password?",
    "AdminAuth.backToHome": "Back to Home",
    "AdminAuth.success": "Success!",
    "AdminAuth.successDesc": "You've been logged in successfully as admin.",
    "AdminAuth.accessDenied": "Access Denied",
    "AdminAuth.accessDeniedDesc": "This login page is for administrators only.",
    "AdminAuth.error": "Error",
    "AdminAuth.errorDesc": "An error occurred during authentication",
    "Reauth.title": "Confirm your identity",
    "Reauth.description": "This action is sensitive. Confirm your identity to continue.",
    "Reauth.passwordLabel": "Password",
    "Reauth.codeLabel": "Authentication code",
    "Reauth.confirm": "Confirm",
    "Reauth.cancel": "Cancel",
    "Reauth.failed": "Verification failed. Try again.",
    "Reauth.checking": "Checking…",
    "deletionRequest.menuLink": "Delete my account",
    "deletionRequest.title": "Request account deletion",
    "deletionRequest.description": "Your request will be sent to your salon for processing.",
    "deletionRequest.legalNotice": "Your account deletion will be handled by the salon. Some data (such as invoices) may be retained to meet legal and tax obligations.",
    "deletionRequest.confirmNameLabel": "Confirm your full name",
    "deletionRequest.confirmNamePlaceholder": "Your first and last name",
    "deletionRequest.confirmCheckbox": "I confirm that I want my account to be deleted.",
    "deletionRequest.submit": "Send request",
    "deletionRequest.nameRequired": "Please enter your full name.",
    "deletionRequest.submitFailed": "Could not send your request. Please try again.",
    "deletionRequest.submitSuccess": "Your deletion request has been sent to your salon.",
    "deletionRequest.pendingNotice": "A deletion request is already being processed. Your salon will handle it shortly.",
    "deletionRequest.owner.bannerTitle": "Account deletion requests",
    "deletionRequest.owner.bannerSubtitle": "Customers who have requested deletion of their account (right to erasure). Review the file, then delete or refuse.",
    "deletionRequest.owner.requestedOn": "Requested on",
    "deletionRequest.owner.viewFile": "View file",
    "deletionRequest.owner.delete": "Delete",
    "deletionRequest.owner.refuse": "Refuse",
    "deletionRequest.owner.refuseTitle": "Refuse this request?",
    "deletionRequest.owner.refuseDescription": "The request will be marked as refused. You can add a note explaining why (e.g. handled offline, identity not confirmed).",
    "deletionRequest.owner.refuseNotePlaceholder": "Reason (optional)",
    "deletionRequest.owner.refuseConfirm": "Refuse request",
    "deletionRequest.owner.refuseSuccess": "Request refused.",
    "deletionRequest.owner.refuseFailed": "Could not refuse the request.",
    "deletionRequest.owner.badge": "to process",

    // Two-Factor Authentication
    "TwoFactor.title": "Two-Factor Authentication",
    "TwoFactor.description": "Protect your admin account with an additional layer of security",
    "TwoFactor.enabled": "2FA Enabled",
    "TwoFactor.disabled": "2FA Disabled",
    "TwoFactor.protectedDesc": "Your account is protected with 2FA",
    "TwoFactor.enableDesc": "Enable 2FA to secure your account",
    "TwoFactor.enableBtn": "Enable 2FA",
    "TwoFactor.disableBtn": "Disable 2FA",
    "TwoFactor.setupTitle": "Setup Two-Factor Authentication",
    "TwoFactor.disableTitle": "Disable Two-Factor Authentication",
    "TwoFactor.disableDesc": "Enter your 2FA code or a backup code to disable two-factor authentication",
    "TwoFactor.verificationCode": "Verification Code",
    "TwoFactor.verificationPlaceholder": "000000 or backup code",
    "TwoFactor.enterCodeError": "Please enter a verification code",
    "TwoFactor.disabledSuccess": "2FA has been disabled",
    "TwoFactor.setupInitiated": "2FA Setup Initiated",
    "TwoFactor.scanQRDesc": "Scan the QR code with your authenticator app",
    "TwoFactor.sixDigitError": "Please enter a 6-digit code",
    "TwoFactor.setupSuccess": "Two-factor authentication has been enabled",
    "TwoFactor.alreadyEnabled": "2FA already enabled",
    "TwoFactor.alreadyEnabledDesc": "Your account already has 2FA enabled.",
    "TwoFactor.setupRequired": "Setup required",
    "TwoFactor.setupRequiredDesc": "Please start setup again to generate a new QR code.",
    "TwoFactor.copied": "Copied!",
    "TwoFactor.secretKeyCopied": "Secret key copied to clipboard",
    "TwoFactor.backupCodesCopied": "Backup codes copied to clipboard",
    "TwoFactor.copyFailed": "Failed to copy to clipboard",
    "TwoFactor.downloaded": "Downloaded",
    "TwoFactor.backupCodesSaved": "Backup codes saved to file",
    "TwoFactor.enableTitle": "Enable Two-Factor Authentication",
    "TwoFactor.enableDescription": "Add an extra layer of security to your admin account",
    "TwoFactor.requiresApp": "2FA requires an authenticator app like Google Authenticator, Authy, or Microsoft Authenticator installed on your mobile device.",
    "TwoFactor.settingUp": "Setting up...",
    "TwoFactor.setup2FA": "Setup 2FA",
    "TwoFactor.scanQRTitle": "Scan QR Code",
    "TwoFactor.scanQRDescription": "Use your authenticator app to scan this QR code",
    "TwoFactor.enterSecretManually": "Or enter this secret key manually:",
    "TwoFactor.saveBackupCodes": "Save your backup codes:",
    "TwoFactor.copyCodes": "Copy Codes",
    "TwoFactor.download": "Download",
    "TwoFactor.enterVerificationCode": "Enter verification code",
    "TwoFactor.verifying": "Verifying...",
    "TwoFactor.verifyAndEnable": "Verify and Enable 2FA",
    "TwoFactor.enabledSuccessfully": "2FA Enabled Successfully!",
    "TwoFactor.accountProtected": "Your account is now protected with two-factor authentication",
    "TwoFactor.saveBackupCodesWarning": "Make sure you've saved your backup codes in a safe place. You'll need them if you lose access to your authenticator app.",
    "TwoFactor.backupCodesFileHeader": "Flowcut AI: 2FA Backup Codes",
    "TwoFactor.backupCodesFileFooter": "Keep these codes in a safe place. Each code can only be used once.",

    // Client cancellation
    "client.cancellationReasonLabel": "Reason for cancellation",
    "client.cancellationReasonPlaceholder": "Optional: help the salon understand why...",
  },
  fr: {

    // Auth toast messages (errorToast / successToast wrapper)
    "Auth.toast.signInFailed": "Impossible de se connecter",
    "Auth.toast.signInSuccess": "Connexion réussie",
    "Auth.toast.signInSuccessDesc": "Bon retour",
    "Auth.toast.signUpFailed": "Impossible de créer le compte",
    "Auth.toast.accountCreated": "Compte créé",
    "Auth.toast.welcomeMessage": "Vous êtes maintenant connecté",
    "Auth.toast.accessDenied": "Accès refusé",
    "Auth.toast.accountReservedAdmin": "Ce compte est réservé à l'administration. Veuillez utiliser un compte client pour réserver.",
    "Auth.toast.invalidPhoneNumber": "Veuillez entrer un numéro de téléphone valide.",
    "Auth.toast.phoneRequired": "Veuillez entrer votre numéro de téléphone.",
    "Auth.toast.salonContextRequired": "Veuillez utiliser le lien de réservation de votre salon pour créer un compte.",
    "InviteAccept.title": "Créez votre compte Flowcut AI",
    "InviteAccept.subtitle": "Bienvenue ! Complétez ces informations pour activer votre abonnement.",
    "InviteAccept.salonName": "Nom du salon",
    "InviteAccept.salonNamePlaceholder": "Salon Élégance",
    "InviteAccept.ownerName": "Votre nom complet",
    "InviteAccept.ownerNamePlaceholder": "Marie Tremblay",
    "InviteAccept.phone": "Téléphone",
    "InviteAccept.email": "Email",
    "InviteAccept.emailLocked": "Le courriel utilisé lors de votre paiement Stripe.",
    "InviteAccept.password": "Mot de passe",
    "InviteAccept.passwordPlaceholder": "Minimum 10 caractères",
    "InviteAccept.passwordHint": "Min. 10 caractères, dont une majuscule, une minuscule, un chiffre et un caractère spécial",
    "InviteAccept.confirmPassword": "Confirmer le mot de passe",
    "InviteAccept.confirmPasswordPlaceholder": "Retapez votre mot de passe",
    "InviteAccept.submit": "Créer mon compte",
    "InviteAccept.submitting": "Création en cours...",
    "InviteAccept.invalidTitle": "Invitation invalide",
    "InviteAccept.invalidDescription": "Ce lien d'invitation a expiré ou n'est plus valide.",
    "InviteAccept.backHome": "Retour à l'accueil",
    "InviteAccept.verifyErrorTitle": "Vérification impossible",
    "InviteAccept.verifyErrorDescription": "Impossible de vérifier votre invitation pour le moment. Il s'agit d'un problème temporaire de notre côté, pas d'un problème avec votre lien.",
    "InviteAccept.retry": "Réessayer",
    "Auth.toast.googleSignInFailed": "Échec de la connexion avec Google. Veuillez réessayer.",
    "Auth.toast.emailVerified": "Courriel vérifié",
    "Auth.toast.verificationEmailSent": "Courriel de vérification renvoyé. Vérifiez votre boîte de réception.",
    "Auth.toast.verificationEmailFailed": "Envoi du courriel impossible. Veuillez réessayer.",
    "Auth.verifyBanner.title": "Veuillez vérifier votre adresse courriel",
    "Auth.verifyBanner.description": "Vérifiez votre boîte de réception pour le lien de confirmation.",
    "Auth.verifyBanner.resend": "Renvoyer",
    "Auth.toast.clockSkew": "L'heure de votre ordinateur semble incorrecte. Activez le réglage automatique de l'heure et du fuseau horaire, puis reconnectez-vous.",
    "Auth.toast.emailVerifiedDesc": "Votre adresse courriel a été confirmée. Vous pouvez maintenant vous connecter.",
    "Auth.toast.passwordResetRequestFailed": "Impossible d'envoyer le courriel de réinitialisation",
    "Auth.toast.passwordResetSent": "Courriel de réinitialisation envoyé",
    "Auth.toast.passwordResetSentDesc": "Les instructions de réinitialisation ont été envoyées à votre courriel.",
    "Auth.toast.passwordResetSuccess": "Mot de passe réinitialisé",
    "Auth.toast.passwordResetSuccessDesc": "Votre mot de passe a été réinitialisé avec succès.",
    "Auth.toast.passwordResetFailed": "Impossible de réinitialiser le mot de passe",
    "Auth.toast.passwordsMismatch": "Les mots de passe ne correspondent pas.",
    "Auth.toast.invalidResetLink": "Le lien de réinitialisation est invalide ou expiré. Veuillez demander un nouveau lien.",
    "Auth.toast.resetLinkExpired": "Votre lien de réinitialisation a expiré. Veuillez en demander un nouveau.",
    "Auth.toast.resetPasswordLeaked": "Ce mot de passe apparaît dans une fuite de données connue. Choisissez-en un autre, votre lien reste valide.",
    "Auth.toast.resetSamePassword": "Ce mot de passe est identique à votre mot de passe actuel. Choisissez-en un autre, votre lien reste valide.",
    "Auth.toast.resetReauthNeeded": "Par sécurité, demandez un nouveau lien de réinitialisation pour changer votre mot de passe.",
    "Auth.toast.missingResetSession": "Session d'authentification manquante. Veuillez cliquer à nouveau sur le lien de réinitialisation.",
    "Auth.toast.twoFactorRequired": "Authentification à deux facteurs requise",
    "Auth.toast.twoFactorRequiredDesc": "Entrez votre code de vérification",
    "Auth.toast.verificationFailed": "Échec de la vérification",
    "Auth.toast.setup2FAFailed": "Impossible de configurer l'authentification à deux facteurs",
    "Auth.toast.disable2FAFailed": "Impossible de désactiver l'authentification à deux facteurs",
    "Auth.toast.accountSuspended": "Compte suspendu",
    "Auth.toast.inviteAcceptFailed": "Impossible d'accepter l'invitation",
    "Auth.toast.inviteAcceptSuccess": "Compte créé avec succès. Redirection...",
    "Auth.toast.passwordTooShort": "Le mot de passe doit contenir au moins 10 caractères",
    "Auth.password.missingUppercase": "Le mot de passe doit contenir au moins une majuscule (A à Z).",
    "Auth.password.missingLowercase": "Le mot de passe doit contenir au moins une minuscule (a à z).",
    "Auth.password.missingDigit": "Le mot de passe doit contenir au moins un chiffre.",
    "Auth.password.missingSpecial": "Le mot de passe doit contenir au moins un caractère spécial, par exemple ! ? # $ % & * -",
    "Auth.passwordRequirements.length": "10 caractères minimum",
    "Auth.passwordRequirements.uppercase": "Une majuscule (A à Z)",
    "Auth.passwordRequirements.lowercase": "Une minuscule (a à z)",
    "Auth.passwordRequirements.digit": "Un chiffre",
    "Auth.passwordRequirements.special": "Un caractère spécial, par exemple ! ? # $ % & * -",
    "Auth.passwordRequirements.met": "(rempli)",
    "Auth.passwordRequirements.unmet": "(manquant)",
    "Auth.toast.captchaRequired": "Veuillez compléter la vérification anti-robot.",
    "Auth.toast.notAuthenticated": "Non authentifié",
    "Auth.toast.createAdminFailed": "Impossible de créer l'admin",
    "Auth.toast.createAdminUnexpected": "Erreur inattendue lors de la création de l'admin",
    "Auth.toast.createAdminSuccess": "Admin créé avec succès",

    // Session
    "session.invalidatedTitle": "Session terminée",
    "session.invalidatedDesc": "Vous avez été déconnecté car votre compte a été accédé depuis un autre appareil.",
    "session.expiredTitle": "Session expirée",
    "session.expiredDesc": "Votre session a expiré après 24 heures. Veuillez vous reconnecter.",
    "session.jwtExpiredTitle": "Session expirée",
    "session.jwtExpiredDesc": "Votre session a expiré. Veuillez vous reconnecter.",

    // ClientAuth
    "ClientAuth.emailPlaceholder": "Courriel",
    "ClientAuth.passwordPlaceholder": "Mot de passe",
    "ClientAuth.forgotPassword": "Mot de passe oublié ?",
    "ClientAuth.loading": "Chargement...",
    "ClientAuth.signIn": "Se connecter",
    "ClientAuth.backToHome": "Retour à l'accueil",
    "ClientAuth.loginSuccessDesc": "Vous vous êtes connecté avec succès.",
    "ClientAuth.otp.codeStepTitle": "Entrez votre code",
    "ClientAuth.otp.codeSentTo": "Nous avons envoyé un code à 6 chiffres à",
    "ClientAuth.otp.codeLabel": "Code de vérification",
    "ClientAuth.otp.verify": "Vérifier",
    "ClientAuth.otp.resend": "Renvoyer le code",
    "ClientAuth.otp.resendIn": "Renvoyer dans",
    "ClientAuth.otp.changeEmail": "Ce n'est pas votre courriel ? Le modifier",
    "ClientAuth.otp.sendFailed": "Impossible d'envoyer le code. Veuillez réessayer.",
    "ClientAuth.otp.rateLimited": "Un code vient déjà d'être envoyé à cette adresse. Vous pourrez en demander un autre dans {seconds} s.",
    "ClientAuth.otp.invalidCode": "Ce code est invalide ou a expiré. Veuillez réessayer.",
    "ClientAuth.wall.titleBook": "Connectez-vous ou inscrivez-vous pour réserver",
    "ClientAuth.wall.titleHistory": "Connectez-vous pour accéder à votre compte",
    "ClientAuth.wall.subtitle": "Pour continuer, nous devrons vérifier qu'il s'agit bien de vous.",
    "ClientAuth.wall.emailHelp": "Nous vous enverrons un code de vérification.",
    "ClientAuth.wall.continue": "Continuer",
    "ClientAuth.pw.title": "Entrez votre mot de passe",
    "ClientAuth.pw.subtitle": "Dernière étape pour confirmer que c'est bien vous.",
    "ClientAuth.setPw.title": "Créez votre mot de passe",
    "ClientAuth.setPw.subtitle": "Votre compte n'a pas encore de mot de passe. Choisissez-en un maintenant et il vous servira pour vos prochaines connexions.",
    "ClientAuth.setPw.submit": "Enregistrer et continuer",
    "ClientAuth.setPw.googleAlt": "Ou, si vous vous connectez habituellement avec Google :",
    "ClientAuth.setPw.refused": "Ce mot de passe a été refusé. Essayez-en un autre.",
    "ClientAuth.password.reauthNeeded": "Par sécurité, confirmez de nouveau votre courriel avant de choisir votre mot de passe.",
    "ClientAuth.setPw.leaked": "Ce mot de passe figure dans une fuite de données connue, il ne peut donc pas être utilisé. Choisissez-en un que vous n'utilisez nulle part ailleurs, par exemple trois mots sans lien entre eux, avec un chiffre et un symbole.",
    // ClientAuthPanel, étape "roleRetry" : la lecture des rôles a échoué (jamais un refus)
    "ClientAuth.roleCheck.title": "Problème de connexion",
    "ClientAuth.roleCheck.failed": "Un problème de connexion nous a empêchés de vérifier votre compte. Veuillez réessayer.",
    "ClientAuth.roleCheck.retry": "Réessayer",
    // /auth/client est login-only : un inconnu ne s'inscrit jamais ici (un
    // client naît d'une réservation, spec §5-bis cas 4). Affiché à la place de
    // CompleteSignupPanel quand onNewClient se déclenche sur cette page autonome.
    "ClientAuth.noAccountFound.title": "Aucun compte trouvé",
    "ClientAuth.noAccountFound.description": "Prenez un rendez-vous pour en créer un.",
    // Flux de code maison (lot 7 du tunnel /book) : un message par refus de l'edge client-email-code.
    "ClientAuth.code.tooManyRequests": "Trop de demandes de code depuis cet appareil. Veuillez patienter quelques minutes avant de réessayer.",
    "ClientAuth.code.serviceBusy": "L'envoi des codes est momentanément indisponible. Veuillez réessayer dans quelques minutes.",
    "ClientAuth.code.captchaFailed": "La vérification anti-robot n'a pas abouti. Veuillez réessayer.",
    "ClientAuth.code.invalidSalon": "Ce lien de réservation n'est plus valide. Veuillez contacter votre salon.",
    "ClientAuth.code.wrong": "Ce code est incorrect. Il vous reste {n} essais.",
    "ClientAuth.code.wrongOne": "Ce code est incorrect. Il vous reste 1 essai.",
    "ClientAuth.code.tooManyAttempts": "Trop d'essais pour ce code. Demandez un nouveau code.",
    "ClientAuth.code.expired": "Ce code a expiré. Demandez un nouveau code.",
    "ClientAuth.code.staffAccount": "Cette adresse appartient à un compte du personnel d'un salon. Pour réserver, utilisez une autre adresse courriel.",
    "ClientAuth.code.sessionFailed": "Votre code est valide, mais la connexion n'a pas pu s'ouvrir. Veuillez réessayer.",
    "ClientAuth.code.network": "Connexion impossible. Vérifiez votre réseau, puis réessayez.",
    "ClientAuth.code.incomplete": "Saisissez les 6 chiffres du code.",
    "ClientAuth.continueWithGoogle": "Continuer avec Google",
    "ClientAuth.or": "OU",
    "ClientAuth.noSalonHint": "Vous n'avez pas de compte ? Utilisez le lien de réservation de votre salon pour vous inscrire.",
    "ClientAuth.backToBooking": "Retour à la réservation",

    // CompleteSignupPanel ("Terminer l'inscription" : monté après ClientAuthPanel.onNewClient)
    "CompleteSignup.title": "Terminer l'inscription",
    "CompleteSignup.subtitleEmail": "Encore quelques détails pour terminer la création de votre compte.",
    "CompleteSignup.subtitleGoogle": "Presque terminé : encore quelques détails.",
    "CompleteSignup.emailLabel": "Courriel",
    "CompleteSignup.fullNameLabel": "Nom complet",
    "CompleteSignup.phoneLabel": "Numéro de téléphone (facultatif)",
    "CompleteSignup.passwordLabel": "Mot de passe",
    "CompleteSignup.submitButton": "Terminer l'inscription",
    "CompleteSignup.success": "Votre compte est prêt !",
    "CompleteSignup.errors.fullNameRequired": "Veuillez entrer votre nom complet.",
    "CompleteSignup.errors.phoneInvalid": "Veuillez entrer un numéro de téléphone valide.",
    "CompleteSignup.errors.saveFailed": "Une erreur est survenue. Veuillez réessayer.",
    "CompleteSignup.errors.profileSaveFailed": "Votre mot de passe est bien enregistré, mais vos informations n'ont pas pu l'être. Veuillez réessayer.",

    // OwnerAuth
    "OwnerAuth.welcomeBack": "Bienvenue",
    "OwnerAuth.signInPrompt": "Connectez-vous pour gérer votre salon",
    "OwnerAuth.email": "Courriel",
    "OwnerAuth.emailPlaceholder": "vous@exemple.com",
    "OwnerAuth.password": "Mot de passe",
    "OwnerAuth.passwordPlaceholder": "••••••••",
    "OwnerAuth.loading": "Chargement...",
    "OwnerAuth.signIn": "Se connecter",
    "OwnerAuth.forgotPassword": "Mot de passe oublié ?",
    "OwnerAuth.backToHome": "Retour à l'accueil",
    "OwnerAuth.success": "Succès !",
    "OwnerAuth.successDesc": "Vous êtes connecté avec succès.",
    "OwnerAuth.accessDenied": "Accès refusé",
    "OwnerAuth.accessDeniedDesc":
      "Cette page de connexion est réservée aux propriétaires de salon. Veuillez utiliser la page de connexion appropriée pour votre rôle.",
    "OwnerAuth.error": "Erreur",
    "OwnerAuth.errorDesc": "Une erreur s'est produite lors de l'authentification",
    "OwnerAuth.accountSuspended": "Compte suspendu : paiement échoué",
    "OwnerAuth.accountSuspendedDesc":
      "Votre compte salon a été suspendu en raison d'un échec de paiement. Veuillez mettre à jour votre méthode de paiement pour restaurer l'accès.",

    // AdminAuth
    "AdminAuth.welcomeBack": "Bienvenue",
    "AdminAuth.signInPrompt": "Connectez-vous pour accéder au tableau de bord",
    "AdminAuth.email": "Courriel",
    "AdminAuth.emailPlaceholder": "admin@exemple.com",
    "AdminAuth.password": "Mot de passe",
    "AdminAuth.passwordPlaceholder": "••••••••",
    "AdminAuth.loading": "Chargement...",
    "AdminAuth.signIn": "Se connecter",
    "AdminAuth.forgotPassword": "Mot de passe oublié ?",
    "AdminAuth.backToHome": "Retour à l'accueil",
    "AdminAuth.success": "Succès !",
    "AdminAuth.successDesc": "Vous êtes connecté avec succès en tant qu'administrateur.",
    "AdminAuth.accessDenied": "Accès refusé",
    "AdminAuth.accessDeniedDesc": "Cette page de connexion est réservée aux administrateurs.",
    "AdminAuth.error": "Erreur",
    "AdminAuth.errorDesc": "Une erreur s'est produite lors de l'authentification",
    "Reauth.title": "Confirmer votre identité",
    "Reauth.description": "Cette action est sensible. Confirmez votre identité pour continuer.",
    "Reauth.passwordLabel": "Mot de passe",
    "Reauth.codeLabel": "Code d'authentification",
    "Reauth.confirm": "Confirmer",
    "Reauth.cancel": "Annuler",
    "Reauth.failed": "Échec de la vérification. Réessayez.",
    "Reauth.checking": "Vérification…",
    "deletionRequest.menuLink": "Supprimer mon compte",
    "deletionRequest.title": "Demande de suppression de compte",
    "deletionRequest.description": "Votre demande sera transmise à votre salon pour traitement.",
    "deletionRequest.legalNotice": "La suppression de votre compte sera traitée par le salon. Certaines données (comme les factures) peuvent être conservées pour respecter des obligations légales et fiscales.",
    "deletionRequest.confirmNameLabel": "Confirmez votre nom complet",
    "deletionRequest.confirmNamePlaceholder": "Votre prénom et nom",
    "deletionRequest.confirmCheckbox": "Je confirme vouloir que mon compte soit supprimé.",
    "deletionRequest.submit": "Envoyer la demande",
    "deletionRequest.nameRequired": "Veuillez entrer votre nom complet.",
    "deletionRequest.submitFailed": "Impossible d'envoyer votre demande. Veuillez réessayer.",
    "deletionRequest.submitSuccess": "Votre demande de suppression a été transmise à votre salon.",
    "deletionRequest.pendingNotice": "Une demande de suppression est déjà en cours de traitement. Votre salon s'en occupera sous peu.",
    "deletionRequest.owner.bannerTitle": "Demandes de suppression de compte",
    "deletionRequest.owner.bannerSubtitle": "Clients ayant demandé la suppression de leur compte (droit à l'effacement). Consultez la fiche, puis supprimez ou refusez.",
    "deletionRequest.owner.requestedOn": "Demandé le",
    "deletionRequest.owner.viewFile": "Voir la fiche",
    "deletionRequest.owner.delete": "Supprimer",
    "deletionRequest.owner.refuse": "Refuser",
    "deletionRequest.owner.refuseTitle": "Refuser cette demande ?",
    "deletionRequest.owner.refuseDescription": "La demande sera marquée comme refusée. Vous pouvez ajouter une note expliquant pourquoi (ex. traitée hors logiciel, identité non confirmée).",
    "deletionRequest.owner.refuseNotePlaceholder": "Motif (facultatif)",
    "deletionRequest.owner.refuseConfirm": "Refuser la demande",
    "deletionRequest.owner.refuseSuccess": "Demande refusée.",
    "deletionRequest.owner.refuseFailed": "Impossible de refuser la demande.",
    "deletionRequest.owner.badge": "à traiter",

    // Two-Factor Authentication
    "TwoFactor.title": "Authentification à deux facteurs",
    "TwoFactor.description": "Protégez votre compte administrateur avec une couche de sécurité supplémentaire",
    "TwoFactor.enabled": "2FA Activé",
    "TwoFactor.disabled": "2FA Désactivé",
    "TwoFactor.protectedDesc": "Votre compte est protégé par le 2FA",
    "TwoFactor.enableDesc": "Activez le 2FA pour sécuriser votre compte",
    "TwoFactor.enableBtn": "Activer le 2FA",
    "TwoFactor.disableBtn": "Désactiver le 2FA",
    "TwoFactor.setupTitle": "Configurer l'authentification à deux facteurs",
    "TwoFactor.disableTitle": "Désactiver l'authentification à deux facteurs",
    "TwoFactor.disableDesc": "Entrez votre code 2FA ou un code de secours pour désactiver l'authentification à deux facteurs",
    "TwoFactor.verificationCode": "Code de vérification",
    "TwoFactor.verificationPlaceholder": "000000 ou code de secours",
    "TwoFactor.enterCodeError": "Veuillez entrer un code de vérification",
    "TwoFactor.disabledSuccess": "Le 2FA a été désactivé",
    "TwoFactor.setupInitiated": "Configuration 2FA lancée",
    "TwoFactor.scanQRDesc": "Scannez le code QR avec votre application d'authentification",
    "TwoFactor.sixDigitError": "Veuillez entrer un code à 6 chiffres",
    "TwoFactor.setupSuccess": "L'authentification à deux facteurs a été activée",
    "TwoFactor.alreadyEnabled": "2FA déjà activé",
    "TwoFactor.alreadyEnabledDesc": "Votre compte a déjà le 2FA activé.",
    "TwoFactor.setupRequired": "Configuration requise",
    "TwoFactor.setupRequiredDesc": "Veuillez relancer la configuration pour générer un nouveau code QR.",
    "TwoFactor.copied": "Copié !",
    "TwoFactor.secretKeyCopied": "Clé secrète copiée dans le presse-papier",
    "TwoFactor.backupCodesCopied": "Codes de secours copiés dans le presse-papier",
    "TwoFactor.copyFailed": "Échec de la copie dans le presse-papier",
    "TwoFactor.downloaded": "Téléchargé",
    "TwoFactor.backupCodesSaved": "Codes de secours enregistrés dans un fichier",
    "TwoFactor.enableTitle": "Activer l'authentification à deux facteurs",
    "TwoFactor.enableDescription": "Ajoutez une couche de sécurité supplémentaire à votre compte administrateur",
    "TwoFactor.requiresApp": "Le 2FA nécessite une application d'authentification comme Google Authenticator, Authy ou Microsoft Authenticator installée sur votre appareil mobile.",
    "TwoFactor.settingUp": "Configuration en cours...",
    "TwoFactor.setup2FA": "Configurer le 2FA",
    "TwoFactor.scanQRTitle": "Scanner le code QR",
    "TwoFactor.scanQRDescription": "Utilisez votre application d'authentification pour scanner ce code QR",
    "TwoFactor.enterSecretManually": "Ou entrez cette clé secrète manuellement :",
    "TwoFactor.saveBackupCodes": "Sauvegardez vos codes de secours :",
    "TwoFactor.copyCodes": "Copier les codes",
    "TwoFactor.download": "Télécharger",
    "TwoFactor.enterVerificationCode": "Entrez le code de vérification",
    "TwoFactor.verifying": "Vérification...",
    "TwoFactor.verifyAndEnable": "Vérifier et activer le 2FA",
    "TwoFactor.enabledSuccessfully": "2FA activé avec succès !",
    "TwoFactor.accountProtected": "Votre compte est maintenant protégé par l'authentification à deux facteurs",
    "TwoFactor.saveBackupCodesWarning": "Assurez-vous d'avoir sauvegardé vos codes de secours dans un endroit sûr. Vous en aurez besoin si vous perdez l'accès à votre application d'authentification.",
    "TwoFactor.backupCodesFileHeader": "Flowcut AI : Codes de secours 2FA",
    "TwoFactor.backupCodesFileFooter": "Conservez ces codes dans un endroit sûr. Chaque code ne peut être utilisé qu'une seule fois.",

    // Client cancellation
    "client.cancellationReasonLabel": "Raison de l'annulation",
    "client.cancellationReasonPlaceholder": "Optionnel : aidez le salon à comprendre pourquoi...",
  },
};
